import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Power, PowerOff, Search, Truck } from "lucide-react";

import { ApiError } from "../../lib/api";
import { coincideBusqueda, pasaEstado, type FiltroEstado } from "./filtros";
import { useToast } from "../../components/Toast";
import { EmptyState } from "../../components/EmptyState";
import { Modal } from "../../components/Modal";
import {
  Badge,
  Button,
  Cargando,
  ConfirmDialog,
  ErrorCarga,
  Input,
  PageHeader,
  TableWrap,
  useDescarteProtegido,
  useHayCambios,
} from "../../components/ui";
import {
  actualizarProveedor,
  crearProveedor,
  listarProveedores,
  type ProveedorCreate,
  type ProveedorRead,
  type ProveedorUpdate,
} from "./api";

export function ProveedoresListPage(): JSX.Element {
  const [editing, setEditing] = useState<ProveedorRead | "new" | null>(null);
  const [q, setQ] = useState("");
  const [estado, setEstado] = useState<FiltroEstado>("activos");

  const proveedoresQ = useQuery({
    queryKey: ["inventario", "proveedores"],
    queryFn: listarProveedores,
  });

  const total = proveedoresQ.data?.length ?? 0;
  const filtrados = useMemo(() => {
    const data = proveedoresQ.data ?? [];
    return data.filter(
      (p) =>
        pasaEstado(p.activo, estado) &&
        coincideBusqueda(
          q,
          p.razon_social,
          p.nombre_fantasia,
          p.rut,
          p.giro,
          p.comuna,
        ),
    );
  }, [proveedoresQ.data, q, estado]);

  return (
    <div className="space-y-4">
      <PageHeader
        title="Proveedores"
        icon={Truck}
        subtitle={proveedoresQ.data && (
          <span>
            ({filtrados.length}
            {filtrados.length !== total ? ` de ${total}` : ""})
          </span>
        )}
        actions={
          <Button icon={Plus} onClick={() => setEditing("new")}>
                    Nuevo proveedor
                  </Button>
        }
      />

      {total > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-500" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar razón social, RUT, giro…"
              className="w-72 rounded-md border border-neutral-450 py-1.5 pl-8 pr-3 text-sm shadow-sm"
            />
          </div>
          <select
            value={estado}
            onChange={(e) => setEstado(e.target.value as FiltroEstado)}
            className="rounded-md border border-neutral-450 px-3 py-1.5 text-sm shadow-sm"
          >
            <option value="activos">Activos</option>
            <option value="inactivos">Inactivos</option>
            <option value="todos">Todos</option>
          </select>
        </div>
      )}

      {editing && (
        <ProveedorForm
          proveedor={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}

      {proveedoresQ.isPending && <Cargando />}
      {proveedoresQ.isError && (
        <ErrorCarga
          error={proveedoresQ.error}
          mensaje="Error al cargar proveedores."
          onReintentar={() => proveedoresQ.refetch()}
        />
      )}

      {proveedoresQ.data && total === 0 && (
        <EmptyState
          icon={Truck}
          title="Sin proveedores todavía"
          description="Registra a quién le compran insumos: cloro, ferretería, EPP…"
        >
          <Button icon={Plus} onClick={() => setEditing("new")}>
            Nuevo proveedor
          </Button>
        </EmptyState>
      )}

      {total > 0 && filtrados.length === 0 && (
        <p className="rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-6 text-center text-sm text-neutral-500">
          Ningún proveedor coincide con el filtro.
        </p>
      )}

      {filtrados.length > 0 && (
        <TableWrap label="Proveedores">
          <table className="min-w-full divide-y divide-neutral-200 text-sm">
            <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th scope="col" className="px-3 py-2 text-left">Razón social</th>
                <th scope="col" className="px-3 py-2 text-left">RUT</th>
                <th scope="col" className="px-3 py-2 text-left">Giro</th>
                <th scope="col" className="px-3 py-2 text-left">Contacto</th>
                <th scope="col" className="px-3 py-2 text-left">Estado</th>
                <th scope="col" className="px-3 py-2 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {filtrados.map((p) => (
                <ProveedorRow
                  key={p.id}
                  proveedor={p}
                  onEdit={() => setEditing(p)}
                />
              ))}
            </tbody>
          </table>
        </TableWrap>
      )}
    </div>
  );
}

function ProveedorRow({
  proveedor,
  onEdit,
}: {
  proveedor: ProveedorRead;
  onEdit: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const [confirming, setConfirming] = useState(false);

  const toggleMut = useMutation({
    mutationFn: (activo: boolean) =>
      actualizarProveedor(proveedor.id, { activo }),
    onSuccess: (prov) => {
      toast.success(
        prov.activo
          ? `Proveedor "${prov.razon_social}" reactivado`
          : `Proveedor "${prov.razon_social}" desactivado`,
      );
      void qc.invalidateQueries({ queryKey: ["inventario", "proveedores"] });
      setConfirming(false);
    },
    onError: (err) => {
      setConfirming(false);
      toast.error(
        err instanceof ApiError ? err.message : "No se pudo actualizar",
      );
    },
  });

  return (
    <tr className="hover:bg-neutral-50">
      <td className="px-3 py-2 font-medium text-neutral-900">
        {proveedor.razon_social}
        {proveedor.nombre_fantasia && (
          <div className="text-xs text-neutral-500 font-normal">
            {proveedor.nombre_fantasia}
          </div>
        )}
      </td>
      <td className="px-3 py-2 font-mono text-xs text-neutral-700">
        {proveedor.rut}
      </td>
      <td className="px-3 py-2 text-neutral-600">{proveedor.giro ?? "—"}</td>
      <td className="px-3 py-2 text-neutral-600">
        <div className="text-xs space-y-0.5">
          {proveedor.contacto_nombre && (
            <div className="text-neutral-700">{proveedor.contacto_nombre}</div>
          )}
          {proveedor.contacto_telefono && (
            <div>{proveedor.contacto_telefono}</div>
          )}
          {proveedor.contacto_email && <div>{proveedor.contacto_email}</div>}
          {!proveedor.contacto_nombre &&
            !proveedor.contacto_telefono &&
            !proveedor.contacto_email &&
            "—"}
        </div>
      </td>
      <td className="px-3 py-2">
        <Badge tone={proveedor.activo ? "emerald" : "slate"}>
          {proveedor.activo ? "Activo" : "Inactivo"}
        </Badge>
      </td>
      <td className="px-3 py-2 text-right">
        <div className="flex items-center justify-end gap-3">
          <Button variant="link" size="sm" icon={Pencil} onClick={onEdit}>
            Editar
          </Button>
          {proveedor.activo ? (
            <Button
              variant="linkDanger"
              size="sm"
              icon={PowerOff}
              onClick={() => setConfirming(true)}
              disabled={toggleMut.isPending}
            >
              Desactivar
            </Button>
          ) : (
            <Button
              variant="link"
              size="sm"
              icon={Power}
              onClick={() => toggleMut.mutate(true)}
              disabled={toggleMut.isPending}
            >
              Activar
            </Button>
          )}
        </div>
      </td>

      {confirming && (
        <ConfirmDialog
          open
          title={`Desactivar proveedor ${proveedor.razon_social}`}
          description="Deja de aparecer en la selección de compras, pero se conserva el historial de movimientos asociados. Puedes reactivarlo más adelante."
          confirmLabel="Desactivar"
          tone="danger"
          loading={toggleMut.isPending}
          onCancel={() => setConfirming(false)}
          onConfirm={() => toggleMut.mutate(false)}
        />
      )}
    </tr>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Form alta/edición
// ────────────────────────────────────────────────────────────────────────

function ProveedorForm({
  proveedor,
  onClose,
}: {
  proveedor: ProveedorRead | null;
  onClose: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const editando = proveedor !== null;

  const [rut, setRut] = useState(proveedor?.rut ?? "");
  const [razonSocial, setRazonSocial] = useState(proveedor?.razon_social ?? "");
  const [nombreFantasia, setNombreFantasia] = useState(
    proveedor?.nombre_fantasia ?? "",
  );
  const [giro, setGiro] = useState(proveedor?.giro ?? "");
  const [direccion, setDireccion] = useState(proveedor?.direccion ?? "");
  const [comuna, setComuna] = useState(proveedor?.comuna ?? "");
  const [telefono, setTelefono] = useState(proveedor?.telefono ?? "");
  const [email, setEmail] = useState(proveedor?.email ?? "");
  const [contactoNombre, setContactoNombre] = useState(
    proveedor?.contacto_nombre ?? "",
  );
  const [contactoTelefono, setContactoTelefono] = useState(
    proveedor?.contacto_telefono ?? "",
  );
  const [contactoEmail, setContactoEmail] = useState(
    proveedor?.contacto_email ?? "",
  );
  const [error, setError] = useState<string | null>(null);

  const crearMut = useMutation({
    mutationFn: (p: ProveedorCreate) => crearProveedor(p),
    onSuccess: (prov) => {
      toast.success(`Proveedor "${prov.razon_social}" creado`);
      void qc.invalidateQueries({ queryKey: ["inventario", "proveedores"] });
      onClose();
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : "No se pudo crear";
      setError(msg);
      toast.error(msg);
    },
  });

  const actualizarMut = useMutation({
    mutationFn: (p: ProveedorUpdate) =>
      actualizarProveedor((proveedor as ProveedorRead).id, p),
    onSuccess: (prov) => {
      toast.success(`Proveedor "${prov.razon_social}" actualizado`);
      void qc.invalidateQueries({ queryKey: ["inventario", "proveedores"] });
      onClose();
    },
    onError: (err) => {
      const msg =
        err instanceof ApiError ? err.message : "No se pudo actualizar";
      setError(msg);
      toast.error(msg);
    },
  });

  const isPending = crearMut.isPending || actualizarMut.isPending;

  function submit(): void {
    setError(null);
    if (!editando && !rut.trim()) {
      const msg = "RUT requerido";
      setError(msg);
      toast.error(msg);
      return;
    }
    if (!razonSocial.trim()) {
      const msg = "Razón social requerida";
      setError(msg);
      toast.error(msg);
      return;
    }

    if (editando) {
      const payload: ProveedorUpdate = {
        razon_social: razonSocial.trim(),
        nombre_fantasia: nombreFantasia.trim() || null,
        giro: giro.trim() || null,
        direccion: direccion.trim() || null,
        comuna: comuna.trim() || null,
        telefono: telefono.trim() || null,
        email: email.trim() || null,
        contacto_nombre: contactoNombre.trim() || null,
        contacto_telefono: contactoTelefono.trim() || null,
        contacto_email: contactoEmail.trim() || null,
      };
      actualizarMut.mutate(payload);
    } else {
      const payload: ProveedorCreate = {
        rut: rut.trim(),
        razon_social: razonSocial.trim(),
        nombre_fantasia: nombreFantasia.trim() || null,
        giro: giro.trim() || null,
        direccion: direccion.trim() || null,
        comuna: comuna.trim() || null,
        telefono: telefono.trim() || null,
        email: email.trim() || null,
        contacto_nombre: contactoNombre.trim() || null,
        contacto_telefono: contactoTelefono.trim() || null,
        contacto_email: contactoEmail.trim() || null,
      };
      crearMut.mutate(payload);
    }
  }

  // Cambios sin guardar: cerrar pregunta antes de descartar (auditoría UX-01).
  const hayCambios = useHayCambios([
    rut, razonSocial, nombreFantasia, giro, direccion, comuna, telefono, email,
    contactoNombre, contactoTelefono, contactoEmail,
  ]);
  const { cerrar: intentarCerrar, dialogo: dialogoDescarte } = useDescarteProtegido(
    hayCambios,
    onClose,
    crearMut.isPending || actualizarMut.isPending,
  );

  return (
    <Modal
      open
      onClose={intentarCerrar}
      title={editando ? "Editar proveedor" : "Nuevo proveedor"}
      subtitle={editando ? proveedor.razon_social : undefined}
      icon={Truck}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={intentarCerrar} disabled={isPending}>
            Cancelar
          </Button>
          <Button onClick={submit} disabled={isPending}>
            {isPending
              ? editando
                ? "Guardando…"
                : "Creando…"
              : editando
                ? "Guardar cambios"
                : "Crear proveedor"}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Input
          label="RUT"
          required
          type="text"
          maxLength={20}
          value={rut}
          onChange={(e) => setRut(e.target.value)}
          disabled={editando || isPending}
          placeholder="ej. 76.123.456-7"
          autoFocus={!editando}
        />
        <Input
          label="Razón social"
          required
          type="text"
          maxLength={200}
          value={razonSocial}
          onChange={(e) => setRazonSocial(e.target.value)}
          disabled={isPending}
          autoFocus={editando}
        />
        <Input
          label="Nombre de fantasía"
          type="text"
          value={nombreFantasia}
          onChange={(e) => setNombreFantasia(e.target.value)}
          disabled={isPending}
        />
        <Input
          label="Giro"
          type="text"
          value={giro}
          onChange={(e) => setGiro(e.target.value)}
          disabled={isPending}
        />
        <Input
          label="Dirección"
          type="text"
          value={direccion}
          onChange={(e) => setDireccion(e.target.value)}
          disabled={isPending}
        />
        <Input
          label="Comuna"
          type="text"
          value={comuna}
          onChange={(e) => setComuna(e.target.value)}
          disabled={isPending}
        />
        <Input
          label="Teléfono"
          type="text"
          value={telefono}
          onChange={(e) => setTelefono(e.target.value)}
          disabled={isPending}
        />
        <Input
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={isPending}
        />
        <Input
          label="Nombre de contacto"
          type="text"
          value={contactoNombre}
          onChange={(e) => setContactoNombre(e.target.value)}
          disabled={isPending}
        />
        <Input
          label="Teléfono de contacto"
          type="text"
          value={contactoTelefono}
          onChange={(e) => setContactoTelefono(e.target.value)}
          disabled={isPending}
        />
        <Input
          label="Email de contacto"
          type="email"
          value={contactoEmail}
          onChange={(e) => setContactoEmail(e.target.value)}
          disabled={isPending}
        />
      </div>

      {error && <p className="mt-3 text-sm text-rose-700">{error}</p>}
      {dialogoDescarte}
    </Modal>
  );
}
